<template>
  <!-- Confirmation Dialog -->
  <ConfirmDialog
    group="scheduleBulkConfirm"
    class="schedule-bulk-confirm-dialog"
  />

  <Dialog
    v-model:visible="isVisible"
    :header="t('flows.scheduleBulk.title')"
    :modal="true"
    :style="{ width: '50rem' }"
    :breakpoints="{ '1200px': '60vw', '700px': '100vw' }"
    class="flow-schedule-bulk-modal"
    position="top"
    :draggable="false"
    @hide="handleClose"
  >
    <!-- Send Type Selection -->
    <div class="grid">
      <div class="col">
        <div
          class="schedule-type-card"
          :class="{ 'active': sendType === 'later' }"
          @click="sendType = 'later'"
        >
          <div class="schedule-type-card__header">
            <div class="schedule-type-card__icon">
              <i class="far fa-calendar-alt" />
            </div>
            <div class="schedule-type-card__title">
              {{ t('flows.scheduleBulk.sendLater.label') }}
            </div>
            <div class="schedule-type-card__radio">
              <i
                class="far"
                :class="sendType === 'later' ? 'fa-circle-dot' : 'fa-circle'"
              />
            </div>
          </div>
          <div class="schedule-type-card__description">
            {{ t('flows.scheduleBulk.sendLater.description') }}
          </div>
        </div>
      </div>
      <div class="col">
        <div
          class="schedule-type-card"
          :class="{ 'active': sendType === 'now' }"
          @click="sendType = 'now'"
        >
          <div class="schedule-type-card__header">
            <div class="schedule-type-card__icon">
              <i class="far fa-paper-plane-top" />
            </div>
            <div class="schedule-type-card__title">
              {{ t('flows.scheduleBulk.sendNow.label') }}
            </div>
            <div class="schedule-type-card__radio">
              <i
                class="far"
                :class="sendType === 'now' ? 'fa-circle-dot' : 'fa-circle'"
              />
            </div>
          </div>
          <div class="schedule-type-card__description">
            {{ t('flows.scheduleBulk.sendNow.description') }}
          </div>
        </div>
      </div>
    </div>

    <!-- Date/Time Selection (only for "later") -->
    <div
      v-if="sendType === 'later'"
      class="grid mt-3"
    >
      <div class="col-4">
        <label class="block mb-2 text-left">
          {{ t('flows.scheduleBulk.date.label') }}
        </label>
        <Calendar
          v-model="selectedDate"
          :min-date="minDate"
          date-format="dd/mm/yy"
          class="w-full"
          show-icon
        />
      </div>
      <div class="col-4">
        <label class="block mb-2 text-left">
          {{ t('flows.scheduleBulk.time.label') }}
        </label>
        <Dropdown
          v-model="selectedTime"
          :options="timeOptions"
          option-label="label"
          option-value="value"
          class="w-full"
        />
      </div>
      <div class="col-4">
        <label class="block mb-2 text-left">
          {{ t('flows.scheduleBulk.timezone.label') }}
        </label>
        <Dropdown
          v-model="selectedTimezone"
          :options="timezoneOptions"
          option-label="label"
          option-value="value"
          option-group-children="items"
          option-group-label="label"
          :filter="true"
          class="w-full"
        />
      </div>
    </div>

    <!-- Perfect Timing -->
    <div class="grid mt-3">
      <div class="col-6">
        <label class="block mb-2 text-left">
          {{ t('flows.scheduleBulk.perfectTiming.label') }}
          <i
            v-tooltip="{ value: t('flows.scheduleBulk.perfectTiming.tooltip'), class: 'tooltip-icons-outside' }"
            class="fas fa-question-circle ml-1"
          />
        </label>
        <SelectButton
          v-model="perfectTiming"
          :options="perfectTimingOptions"
          option-label="label"
          option-value="value"
          class="w-full"
        />
      </div>
    </div>

    <!-- Slowdown (only when perfect timing is disabled) -->
    <div
      v-if="perfectTiming === 0"
      class="grid mt-3"
    >
      <div class="col-6">
        <label class="block mb-2 text-left">
          {{ t('flows.scheduleBulk.slowdown.label') }}
          <i
            v-tooltip="{ value: t('flows.scheduleBulk.slowdown.tooltip'), class: 'tooltip-icons-outside' }"
            class="fas fa-question-circle ml-1"
          />
        </label>
        <SelectButton
          v-model="slowdown"
          :options="slowdownOptions"
          option-label="label"
          option-value="value"
          class="w-full"
        />
      </div>
      <div
        v-if="slowdown === 1"
        class="col-6"
      >
        <label class="block mb-2 text-left">
          {{ t('flows.scheduleBulk.interval.label') }}
        </label>
        <Dropdown
          v-model="intervalBetweenSends"
          :options="intervalOptions"
          option-label="label"
          option-value="value"
          class="w-full"
        />
      </div>
    </div>

    <template #footer>
      <SpmButton
        :label="t('flows.scheduleBulk.cancel')"
        class-style="p-button p-button-secondary"
        :disabled="loading"
        @click="handleClose"
      />
      <SpmButton
        :label="sendType === 'now' ? t('flows.scheduleBulk.sendNowButton') : t('flows.scheduleBulk.scheduleButton')"
        class-style="p-button p-button-success"
        :loading="loading"
        @click="handleSchedule"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, onMounted,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import Calendar from 'primevue/calendar';
import Dropdown from 'primevue/dropdown';
import SelectButton from 'primevue/selectbutton';
import Tooltip from 'primevue/tooltip';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import moment from 'moment-timezone';

export default defineComponent({
  name: 'FlowScheduleBulkModal',

  components: {
    Dialog,
    ConfirmDialog,
    Calendar,
    Dropdown,
    SelectButton,
    SpmButton,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },

    loading: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['close', 'schedule'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const confirm = useConfirm();

    const isVisible = computed({
      get: () => props.visible,
      set: (value) => {
        if (!value) {
          emit('close');
        }
      },
    });

    // Send type: 'later' or 'now'
    const sendType = ref<'later' | 'now'>('later');

    // Date selection
    const minDate = new Date();
    const selectedDate = ref(new Date());

    // Time options (15 min intervals)
    const timeOptions = ref<{ value: string; label: string }[]>([]);
    for (let i = 0; i < 24; i++) {
      for (let j = 0; j < 60; j += 15) {
        const hours = String(i).padStart(2, '0');
        const minutes = String(j).padStart(2, '0');
        const timeString = `${hours}:${minutes}`;
        timeOptions.value.push({ value: timeString, label: timeString });
      }
    }
    const selectedTime = ref('08:00');

    // Timezone options
    const timezoneOptions = ref<{ label: string; code: string; items: { value: string; label: string }[] }[]>([]);
    const selectedTimezone = ref(moment.tz.guess() || 'UTC');

    const populateTimezoneOptions = () => {
      const allowedContinents = [
        'US', 'Europe', 'Africa', 'UTC', 'Indian', 'Canada', 'Brazil', 'Australia', 'Atlantic', 'Asia',
      ];

      const zones = moment.tz.names();
      const grouped: Record<string, string[]> = {};

      zones.forEach((zone) => {
        const parts = zone.split('/');
        const continent = parts[0];

        if (!allowedContinents.includes(continent)) {
          return;
        }

        if (!grouped[continent]) {
          grouped[continent] = [];
        }

        grouped[continent].push(zone);
      });

      Object.keys(grouped).forEach((continent) => {
        const continentTimezones = grouped[continent];
        const continentTimezonesOptions = continentTimezones.map((timezone: string) => {
          const timezoneLabel = timezone.replace(/_/g, ' ');
          return { value: timezone, label: timezoneLabel };
        });

        timezoneOptions.value.push({
          label: continent,
          code: continent,
          items: continentTimezonesOptions,
        });
      });

      // Add UTC as suggestion at the top
      const clientTimezone = moment.tz.guess();
      const suggestions = [{ value: 'UTC', label: 'UTC' }];
      if (clientTimezone && clientTimezone !== 'UTC') {
        suggestions.push({ value: clientTimezone, label: clientTimezone.replace(/_/g, ' ') });
      }

      timezoneOptions.value.unshift({
        label: t('flows.scheduleBulk.timezone.suggestion'),
        code: 'Suggestion',
        items: suggestions,
      });
    };

    // Perfect timing
    const perfectTiming = ref(0);
    const perfectTimingOptions = computed(() => [
      { value: 1, label: t('flows.scheduleBulk.perfectTiming.activate') },
      { value: 0, label: t('flows.scheduleBulk.perfectTiming.deactivate') },
    ]);

    // Slowdown
    const slowdown = ref(0);
    const slowdownOptions = computed(() => [
      { value: 1, label: t('yes') },
      { value: 0, label: t('no') },
    ]);

    // Interval between sends
    const intervalBetweenSends = ref('15');
    const intervalOptions = computed(() => [
      { value: '15', label: t('flows.scheduleBulk.interval.options.15') },
      { value: '30', label: t('flows.scheduleBulk.interval.options.30') },
      { value: '60', label: t('flows.scheduleBulk.interval.options.60') },
      { value: '90', label: t('flows.scheduleBulk.interval.options.90') },
      { value: '120', label: t('flows.scheduleBulk.interval.options.120') },
      { value: '150', label: t('flows.scheduleBulk.interval.options.150') },
      { value: '180', label: t('flows.scheduleBulk.interval.options.180') },
      { value: '210', label: t('flows.scheduleBulk.interval.options.210') },
      { value: '240', label: t('flows.scheduleBulk.interval.options.240') },
      { value: '270', label: t('flows.scheduleBulk.interval.options.270') },
      { value: '300', label: t('flows.scheduleBulk.interval.options.300') },
    ]);

    const handleClose = () => {
      emit('close');
    };

    const executeSchedule = () => {
      let timeSending: string;

      if (sendType.value === 'later') {
        const dateStr = moment(selectedDate.value as Date).format('YYYY-MM-DD');
        const dateTimeStr = `${dateStr} ${selectedTime.value}:00`;
        const clientMoment = moment.tz(dateTimeStr, selectedTimezone.value);
        const utcMoment = clientMoment.clone().utc();
        timeSending = utcMoment.format('YYYYMMDDHHmmss');
      } else {
        timeSending = moment.utc().format('YYYYMMDDHHmmss');
      }

      const scheduleParams = {
        send_type: sendType.value,
        time_sending: timeSending,
        timezone: selectedTimezone.value,
        perfect_timing: perfectTiming.value,
        slowdown: slowdown.value,
        interval_between_sends: slowdown.value === 1 ? intervalBetweenSends.value : null,
      };

      emit('schedule', scheduleParams);
    };

    const handleSchedule = () => {
      confirm.require({
        group: 'scheduleBulkConfirm',
        header: t('flows.scheduleBulk.confirm.header'),
        message: t('flows.scheduleBulk.confirm.message'),
        icon: 'far fa-calendar-check',
        acceptLabel: t('flows.scheduleBulk.confirm.accept'),
        rejectLabel: t('flows.scheduleBulk.confirm.reject'),
        accept: () => {
          executeSchedule();
        },
      });
    };

    onMounted(() => {
      populateTimezoneOptions();
    });

    return {
      t,
      isVisible,
      sendType,
      minDate,
      selectedDate,
      selectedTime,
      timeOptions,
      selectedTimezone,
      timezoneOptions,
      perfectTiming,
      perfectTimingOptions,
      slowdown,
      slowdownOptions,
      intervalBetweenSends,
      intervalOptions,
      handleClose,
      handleSchedule,
    };
  },
});
</script>

<style lang="scss">
.flow-schedule-bulk-modal {
  .schedule-type-card {
    position: relative;
    border: solid 1px #BABFC3;
    transition: background-color 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s;
    border-radius: 6px;
    padding: 1rem;
    min-height: 5rem;
    cursor: pointer;

    &.active {
      background-color: $brand-color-primary;
      color: #fff;
      border-color: $brand-color-primary;
    }

    &:hover:not(.active) {
      border-color: $brand-color-primary;
    }

    &__header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &__icon {
      i {
        font-size: 1.25rem;
      }
    }

    &__title {
      font-weight: 600;
      flex: 1;
    }

    &__radio {
      i {
        font-size: 1rem;
      }
    }

    &__description {
      margin-top: 0.5rem;
      font-size: 0.85rem;
      opacity: 0.9;
    }
  }

  .grid {
    label {
      font-weight: normal;
      color: #6c757d;
      font-size: 0.95rem;
    }
  }

  .p-selectbutton {
    display: flex;

    .p-button {
      flex: 1;
      justify-content: center;
    }
  }
}

.schedule-bulk-confirm-dialog {
  max-width: 450px !important;
  width: 450px !important;

  .p-confirm-dialog-message {
    margin-left: 0.5rem;
  }
}
</style>
